import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { X, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { FilterState } from './SearchFilters';
import { clampRadiusValue } from '@/lib/loadSearch';
import { parseISO } from 'date-fns';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

interface SavedSearch {
  id: string;
  name: string;
  origin: string;
  destination: string;
  dho_radius: number;
  dhd_radius: number;
  equipment_types: string[];
  load_type: string;
  date_range: string[];
  hide_canada: boolean;
  search_back: number;
  created_at: string;
}

interface SavedSearchesTabStripProps {
  onLoadSearch: (filterState: FilterState) => void;
  refreshTrigger?: number;
}

export const SavedSearchesTabStrip: React.FC<SavedSearchesTabStripProps> = ({
  onLoadSearch,
  refreshTrigger,
}) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSavedSearches = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setIsLoading(false); return; }

      const { data, error } = await supabase
        .from('search_presets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedSearches(data || []);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedSearches();
    const channel = supabase
      .channel('saved_searches_tab_strip')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'search_presets' }, () => {
        fetchSavedSearches();
      })
      .subscribe((status) => { if (status === 'SUBSCRIBED') fetchSavedSearches(); });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; fetchSavedSearches(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === 'visible') runDebounced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, []);


  useEffect(() => {
    if (refreshTrigger !== undefined) fetchSavedSearches();
  }, [refreshTrigger]);

  const handleClick = (search: SavedSearch) => {
    setActiveId(search.id);
    const filterState: FilterState = {
      origin: search.origin || '',
      destination: search.destination || '',
      dhoRadius: clampRadiusValue(search.dho_radius, 150),
      dhdRadius: clampRadiusValue(search.dhd_radius, 150),
      selectedEquipment: search.equipment_types || [],
      loadType: (search.load_type as 'full' | 'partial') || 'full',
      dateRange: (search.date_range || []).map(d => parseISO(d)),
      hideCanada: search.hide_canada,
      searchBack: search.search_back,
    };
    onLoadSearch(filterState);
    toast.success(`Loaded "${search.name}"`);
  };

  const handleDelete = async (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedSearches(prev => prev.filter(s => s.id !== id));
    if (activeId === id) setActiveId(null);

    try {
      const { error } = await supabase.from('search_presets').delete().eq('id', id);
      if (error) { fetchSavedSearches(); throw error; }
      toast.success(`Deleted "${name}"`);
    } catch (error) {
      console.error('Error deleting search:', error);
      toast.error('Failed to delete search');
    }
  };

  const getLabel = (search: SavedSearch) => {
    if (search.origin && search.destination) {
      return `${search.origin} → ${search.destination}`;
    }
    if (search.origin) return search.origin;
    if (search.destination) return `→ ${search.destination}`;
    return search.name;
  };

  if (isLoading || savedSearches.length === 0) return null;

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
        <Star className="h-3.5 w-3.5 fill-current text-brand-amber" />
        <span className="text-[10px] font-bold uppercase tracking-wide">Saved</span>
      </div>
      <ScrollArea className="flex-1 w-0">
        <div className="flex items-center gap-1.5 pb-1">
          {savedSearches.map((search) => (
            <button
              key={search.id}
              onClick={() => handleClick(search)}
              className={cn(
                "group relative flex items-center gap-1.5 shrink-0",
                "rounded-md px-3 py-1.5 text-[11px] font-medium",
                "border transition-all duration-150 cursor-pointer",
                "hover:shadow-sm",
                activeId === search.id
                  ? "bg-brand-teal text-primary-foreground border-brand-teal shadow-sm"
                  : "bg-card text-foreground border-border hover:border-brand-teal/50 hover:bg-muted/50"
              )}
            >
              <span className="whitespace-nowrap">{getLabel(search)}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => handleDelete(search.id, search.name, e)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(search.id, search.name, e as any); }}
                className={cn(
                  "inline-flex items-center justify-center rounded-full h-4 w-4 shrink-0",
                  "opacity-0 group-hover:opacity-100 transition-opacity",
                  activeId === search.id
                    ? "hover:bg-primary-foreground/20 text-primary-foreground"
                    : "hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                )}
              >
                <X className="h-3 w-3" />
              </span>
            </button>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="h-1" />
      </ScrollArea>
    </div>
  );
};
